// [PURPOSE]: Loot-grant pipeline — translates a structured roll-table
//   entry (the {kind, ref, qty} schema from #033) into a structured
//   mutation describing what to apply to the recipient character.
//
// [WHY]: Phase B / #034 (B4-2). Pre-#034 a roll on a custom roll
//   table produced an activity-log line but didn't deposit anything
//   into the recipient's inventory. The DM would say "you got a
//   Longsword +1" and then have to manually add it.
//
// [DESIGN]: The helper is PURE — it returns the mutation as an
//   object without touching any state. Callers apply the mutation
//   themselves via the right context method (setPlayerStats,
//   addInventoryItem, etc.) so the pipeline stays compatible with
//   both local and party-broadcast paths. A second helper,
//   `applyGrantToCharacter`, does the immutable apply for callers
//   that want a one-shot path.
//
// [INTEGRATION]: DMToolsPanel / loot-push call sites consume the
//   mutation. App.jsx wraps with the React state setter when the
//   grant is local; PartyContext serializes the mutation for
//   broadcast when the grant goes to a party member.
//
// [SCHEMA]: See `src/utils/rollTableEntrySchema.js` for the
//   `{kind, ref, qty}` entry shape this consumes.

import { ENTRY_KINDS } from './rollTableEntrySchema';

/**
 * Translate a structured roll-table entry into a mutation object.
 *
 * @param {object|null} entry — `{min, max, kind, ref, qty?, ...}`
 * @returns {object|null} mutation `{ inventoryAdd?, currencyAdd?,
 *   conditionAdd?, featAdd?, spellAdd?, activityLog }`, or null when
 *   the entry is malformed / unknown kind.
 */
export function grantRollResult(entry) {
  if (!entry || typeof entry !== 'object') return null;
  if (!entry.kind) return null;

  switch (entry.kind) {
    case ENTRY_KINDS.ITEM:
      return _grantItem(entry);
    case ENTRY_KINDS.CURRENCY:
      return _grantCurrency(entry);
    case ENTRY_KINDS.CONDITION:
      return _grantCondition(entry);
    case ENTRY_KINDS.STRING_ROLL:
      return _grantStringRoll(entry);
    case ENTRY_KINDS.FEAT:
      return _grantFeat(entry);
    case ENTRY_KINDS.SPELL:
      return _grantSpell(entry);
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Per-kind grant builders
// ---------------------------------------------------------------------------

function _grantItem(entry) {
  // Template-driven entries from rollFromTable carry a `resolved`
  // object with the random base item picked at roll time. Use that
  // resolved id when present; otherwise fall back to the entry's
  // `ref` (the flat-ID dispatch tag).
  const resolved = entry.resolved || null;
  const baseType = resolved?.id || entry.ref || entry.item;
  const name = resolved?.name || entry.name || entry.ref || baseType;
  const qty = typeof entry.qty === 'number' && entry.qty > 0 ? entry.qty : 1;

  // Build a minimal inventory-item shape. The InventoryContext
  // addInventoryItem path will fill in BASE_ITEMS metadata when the
  // item is actually added, so we only need to carry the dispatch
  // ref + the flavor fields.
  const item = {
    baseType,
    name,
    quantity: qty,
    rarity: entry.rarity,
  };
  // Pass through resolved-template properties (magicalBonus, etc.)
  // so the InventoryItem constructor receives the magic +N stamp.
  if (resolved) {
    if (resolved.magicalBonus !== undefined) item.magicalBonus = resolved.magicalBonus;
    if (resolved.statModifications !== undefined) item.statModifications = resolved.statModifications;
    if (resolved.requiresAttunement !== undefined) item.requiresAttunement = resolved.requiresAttunement;
  }

  return {
    inventoryAdd: [item],
    activityLog: qty > 1 ? `${qty}× ${name}` : name,
  };
}

function _grantCurrency(entry) {
  // ref is either {type, amount} OR a {gp, sp, cp, ...} object.
  const ref = entry.ref;
  if (!ref || typeof ref !== 'object') return null;

  let currencyAdd;
  if (typeof ref.type === 'string' && typeof ref.amount === 'number') {
    currencyAdd = { [ref.type]: ref.amount };
  } else {
    currencyAdd = { ...ref };
  }

  // Build a readable activity-log line: "50 gp" or "25 gp + 100 sp"
  const parts = Object.entries(currencyAdd)
    .filter(([, amt]) => amt !== 0)
    .map(([t, amt]) => `${amt} ${t}`);
  const activityLog = parts.length > 0 ? parts.join(' + ') : '0 currency';

  return {
    currencyAdd,
    activityLog,
  };
}

function _grantCondition(entry) {
  const conditionAdd = { id: entry.ref };
  if (entry.dc !== undefined) conditionAdd.dc = entry.dc;
  if (entry.saveType !== undefined) conditionAdd.saveType = entry.saveType;
  if (entry.duration !== undefined) conditionAdd.duration = entry.duration;

  return {
    conditionAdd,
    activityLog: `Condition: ${entry.ref}${entry.dc ? ` (DC ${entry.dc} ${entry.saveType || ''})` : ''}`,
  };
}

function _grantStringRoll(entry) {
  return {
    activityLog: entry.ref || entry.name || '(empty roll)',
  };
}

function _grantFeat(entry) {
  return {
    featAdd: entry.ref,
    activityLog: `Feat granted: ${entry.ref}`,
  };
}

function _grantSpell(entry) {
  return {
    spellAdd: entry.ref,
    activityLog: `Spell granted: ${entry.ref}`,
  };
}

// ---------------------------------------------------------------------------
// applyGrantToCharacter — immutable apply for callers that want a one-shot
// ---------------------------------------------------------------------------

/**
 * Apply a mutation to a character object immutably (returns a new object).
 *
 * Note: this only handles the simple cases — inventory append, currency
 * add. For condition / feat / spell grants the caller must use the
 * proper context methods (effectRegistry.register, etc.) since those
 * involve side effects beyond the character object.
 *
 * @param {object} character
 * @param {object|null} grant
 * @returns {object} new character (or same reference if grant is null)
 */
export function applyGrantToCharacter(character, grant) {
  if (!grant) return character;
  if (!character || typeof character !== 'object') return character;

  let next = character;

  if (Array.isArray(grant.inventoryAdd) && grant.inventoryAdd.length > 0) {
    const prevInventory = Array.isArray(next.inventory) ? next.inventory : [];
    next = { ...next, inventory: [...prevInventory, ...grant.inventoryAdd] };
  }

  if (grant.currencyAdd && typeof grant.currencyAdd === 'object') {
    const prevCurrencies = next.currencies && typeof next.currencies === 'object'
      ? next.currencies
      : {};
    const newCurrencies = { ...prevCurrencies };
    for (const [type, amount] of Object.entries(grant.currencyAdd)) {
      const existing = typeof newCurrencies[type] === 'number' ? newCurrencies[type] : 0;
      newCurrencies[type] = existing + (typeof amount === 'number' ? amount : 0);
    }
    next = { ...next, currencies: newCurrencies };
  }

  // condition / feat / spell additions are NOT applied here — they
  // require side effects (effectRegistry, condition system) that can't
  // be expressed as an immutable character object update. The caller
  // dispatches those via the right context method.
  return next;
}
